import apiClient from './apiClient';
import { Meetup, CreateMeetupData, UpdateMeetupData, MeetupFilters, MeetupSortOptions, Location } from '../types/meetup.types';
import { ParticipantResponse } from '../types/meetup-details.types';
import { apiTrackingService } from './apiTrackingService';
import { cacheService, cacheKeys } from './cacheService';

interface ApiResponse<T> {
  data: T;
}

interface Category {
  id: string;
  name: string;
}

export const meetupApi = {
  // Search meetups with text query and optional filters
  searchMeetups: async (params: {
    q: string;
    category?: string;
    lat?: number;
    lng?: number;
    radius?: number;
    priceMin?: number;
    priceMax?: number;
  }) => {
    const searchParams = new URLSearchParams();
    searchParams.append('q', params.q);
    if (params.category) searchParams.append('category', params.category);
    if (params.lat !== undefined) searchParams.append('lat', params.lat.toString());
    if (params.lng !== undefined) searchParams.append('lng', params.lng.toString());
    if (params.radius !== undefined) searchParams.append('radius', params.radius.toString());
    if (params.priceMin !== undefined) searchParams.append('price_min', params.priceMin.toString());
    if (params.priceMax !== undefined) searchParams.append('price_max', params.priceMax.toString());

    const response = await apiClient.api.get<ApiResponse<Meetup[]>>(`/v1/meetups/search?${searchParams.toString()}`);
    return response.data.data;
  },

  // Get all meetups with optional filters, sorting, and AbortSignal for cancellation
  getMeetups: async (filters?: MeetupFilters, sort?: MeetupSortOptions, signal?: AbortSignal) => {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.category) params.append('category', filters.category);
      if (filters.tags) params.append('tags', filters.tags.join(','));
      if (filters.dateRange) {
        params.append('startDate', filters.dateRange.start);
        params.append('endDate', filters.dateRange.end);
      }
      if (filters.location) {
        params.append('lat', filters.location.lat.toString());
        params.append('lng', filters.location.lng.toString());
        params.append('radius', filters.location.radius.toString());
      }
      if (filters.status) params.append('status', filters.status);
      if (filters.privacy) params.append('privacy', filters.privacy);
      if (typeof filters.upcoming === 'boolean') params.append('upcoming', filters.upcoming.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
    }

    if (sort) {
      params.append('sortField', sort.field);
      params.append('sortDirection', sort.direction);
    }

    const response = await apiClient.api.get<ApiResponse<Meetup[]>>(`/v1/meetups?${params.toString()}`, { signal });
    return response.data.data;
  },

  // Get a single meetup by ID
  getMeetup: async (id: string) => {
    const response = await apiClient.api.get<ApiResponse<Meetup>>(`/v1/meetups/${id}`);
    return response.data.data;
  },

  // OPTIMIZED: Get complete meetup data in a single API call
  // Returns meetup details, participants, centroid, and search radius
  getMeetupFull: async (id: string) => {
    const response = await apiClient.api.get<ApiResponse<{
      meetup: Meetup;
      participants: Array<{
        user_id: string;
        first_name: string;
        last_name: string;
        email: string;
        avatar_url: string | null;
        participant_type: 'creator' | 'registered' | 'guest';
        status: string;
        invitation_status: string;
        location_lat: number | null;
        location_lng: number | null;
        location_address: string | null;
        role: string;
      }>;
      centroid: { latitude: number; longitude: number; participantCount: number } | null;
      searchRadius: number;
    }>>(`/v1/meetups/${id}/full`);
    return response.data.data;
  },

  // Create a new meetup
  createMeetup: async (data: CreateMeetupData) => {
    const response = await apiClient.api.post<ApiResponse<Meetup>>('/v1/meetups', data);
    return response.data.data;
  },

  // Update an existing meetup
  updateMeetup: async (id: string, data: UpdateMeetupData) => {
    const response = await apiClient.api.put<ApiResponse<Meetup>>(`/v1/meetups/${id}`, data);

    // Invalidate cache after successful update
    cacheService.delete(cacheKeys.meetup(id));
    cacheService.delete(cacheKeys.meetupParticipants(id));
    cacheService.delete(cacheKeys.meetupPlaceDetails(id));

    return response.data.data;
  },

  // Delete a meetup
  deleteMeetup: async (id: string) => {
    await apiClient.api.delete(`/v1/meetups/${id}`);

    // Invalidate cache after successful delete
    cacheService.delete(cacheKeys.meetup(id));
    cacheService.delete(cacheKeys.meetupParticipants(id));
    cacheService.delete(cacheKeys.meetupPlaceDetails(id));
  },

  // Join a meetup
  joinMeetup: async (id: string) => {
    const response = await apiClient.api.post<ApiResponse<Meetup>>(`/v1/meetups/${id}/join`);

    // Invalidate cache after successful join
    cacheService.delete(cacheKeys.meetup(id));
    cacheService.delete(cacheKeys.meetupParticipants(id));

    return response.data.data;
  },

  // Leave a meetup
  leaveMeetup: async (id: string) => {
    const response = await apiClient.api.post<ApiResponse<Meetup>>(`/v1/meetups/${id}/leave`);

    // Invalidate cache after successful leave
    cacheService.delete(cacheKeys.meetup(id));
    cacheService.delete(cacheKeys.meetupParticipants(id));

    return response.data.data;
  },

  // Remove a participant from a meetup
  removeParticipant: async (meetupId: string, userId: string) => {
    await apiClient.api.delete(`/v1/meetups/${meetupId}/participants/${userId}`);

    // Invalidate cache after successful participant removal
    cacheService.delete(cacheKeys.meetup(meetupId));
    cacheService.delete(cacheKeys.meetupParticipants(meetupId));
  },

  // Add a comment to a meetup
  addComment: async (meetupId: string, content: string) => {
    const response = await apiClient.api.post<ApiResponse<Meetup>>(`/v1/meetups/${meetupId}/comments`, { content });
    return response.data.data;
  },

  // Delete a comment
  deleteComment: async (meetupId: string, commentId: string) => {
    const response = await apiClient.api.delete<ApiResponse<Meetup>>(`/v1/meetups/${meetupId}/comments/${commentId}`);
    return response.data.data;
  },

  // Get meetup categories
  getCategories: async () => {
    const response = await apiClient.api.get<ApiResponse<Category[]>>('/v1/meetups/categories');
    return response.data.data;
  },

  // Get popular tags
  getTags: async () => {
    const response = await apiClient.api.get<ApiResponse<string[]>>('/v1/meetups/tags');
    return response.data.data;
  },

  // Select a location for the meetup
  selectLocation: async (meetupId: string, locationData: {
    place_id: string;
    name: string;
    address?: string;
    vicinity?: string;
    latitude: number;
    longitude: number;
    rating?: number;
    price_level?: number;
    types?: string[];
  }) => {
    const response = await apiClient.api.post<ApiResponse<{
      meetup: Meetup;
      location: Location;
      notifiedParticipants: number;
    }>>(`/v1/meetups/${meetupId}/select-location`, locationData);
    return response.data;
  },

  // Get participants for a meetup
  getParticipants: async (meetupId: string): Promise<ParticipantResponse[]> => {
    const response = await apiClient.api.get<ApiResponse<ParticipantResponse[]>>(`/v1/meetups/${meetupId}/participants`);
    return response.data.data;
  },

  // Get participant locations for a meetup
  getMeetupParticipantLocations: async (meetupId: string) => {
    const response = await apiClient.api.get(`/v1/meetups/${meetupId}/locations`);
    return response.data; // Return full response including centroid and searchRadius
  },

  // Get meetup suggestions with full filter support
  getMeetupSuggestions: async (
    meetupId: string,
    filters?: {
      limit?: number;
      radius?: number; // in meters
      cuisines?: string[];
      category?: string;
      minRating?: number;
      priceMin?: number;
      priceMax?: number;
      openAtMeetupTime?: boolean;
      keyword?: string;
      page?: number;
      accessibility?: string[];
      atmosphere?: string[];
      dietaryRestrictions?: string[];
      centroidLat?: number;
      centroidLng?: number;
      centroidSource?: string;
    }
  ) => {
    const params = new URLSearchParams();

    // Basic parameters
    params.append('limit', (filters?.limit ?? 100).toString());
    if (filters?.radius) {
      params.append('radius', filters.radius.toString());
    }

    // Cuisine filters
    if (filters?.cuisines && filters.cuisines.length > 0) {
      params.append('cuisines', filters.cuisines.join(','));
    }

    // Category filter
    if (filters?.category && filters.category !== 'all') {
      params.append('category', filters.category);
    }

    // Rating filter
    if (filters?.minRating && filters.minRating > 0) {
      params.append('minRating', filters.minRating.toString());
    }

    // Price filters
    if (filters?.priceMin && filters.priceMin > 1) {
      params.append('priceMin', filters.priceMin.toString());
    }
    if (filters?.priceMax && filters.priceMax < 4) {
      params.append('priceMax', filters.priceMax.toString());
    }

    // Open at meetup time filter
    if (filters?.openAtMeetupTime) {
      params.append('openAtMeetupTime', 'true');
    }

    // Keyword search
    if (filters?.keyword) {
      params.append('keyword', filters.keyword);
    }

    // Pagination
    if (filters?.page && filters.page > 1) {
      params.append('page', filters.page.toString());
    }

    // Accessibility filters
    if (filters?.accessibility && filters.accessibility.length > 0) {
      params.append('accessibility', filters.accessibility.join(','));
    }

    // Atmosphere filters
    if (filters?.atmosphere && filters.atmosphere.length > 0) {
      params.append('atmosphere', filters.atmosphere.join(','));
    }

    // Dietary restrictions
    if (filters?.dietaryRestrictions && filters.dietaryRestrictions.length > 0) {
      params.append('dietaryRestrictions', filters.dietaryRestrictions.join(','));
    }

    // Centroid override (for map-based searching)
    if (filters?.centroidLat !== undefined && filters?.centroidLng !== undefined) {
      params.append('centroidLat', filters.centroidLat.toString());
      params.append('centroidLng', filters.centroidLng.toString());
      if (filters?.centroidSource) {
        params.append('centroidSource', filters.centroidSource);
      }
    }

    // Track that this will trigger Distance Matrix API calls on the backend
    apiTrackingService.trackDistanceMatrix(['participants'], ['suggestions'], false);
    const response = await apiClient.api.get(`/v1/meetups/${meetupId}/suggestions?${params.toString()}`);
    return response.data.data;
  },

  // Update participant location
  updateParticipantLocation: async (meetupId: string, locationData: {
    latitude: number;
    longitude: number;
    address?: string;
  }) => {
    const response = await apiClient.api.put(`/v1/meetups/${meetupId}/location`, locationData);
    return response.data.data;
  },

  // Debug: Fill meetup with participants
  fillParticipants: async (meetupId: string) => {
    const response = await apiClient.api.post(`/v1/meetups/${meetupId}/fill-participants`);
    return response.data;
  },

  // Invite friends, groups, and/or emails to a meetup
  inviteToMeetup: async (meetupId: string, data: {
    friendIds?: string[];
    groupIds?: string[];
    emails?: string[];
    message?: string;
  }) => {
    const response = await apiClient.api.post(`/v1/meetups/${meetupId}/invite`, data);
    return response.data;
  },

  // Check if an email is already invited to a meetup
  checkGuestEmail: async (meetupId: string, email: string) => {
    const response = await apiClient.api.get(`/v1/guest/check-email/${meetupId}/${encodeURIComponent(email)}`);
    return response.data.data;
  },

  // Send guest invitation (for people without accounts)
  sendGuestInvitation: async (meetupId: string, data: {
    name: string;
    email: string;
    message?: string;
  }) => {
    const response = await apiClient.api.post(`/v1/guest/invite`, {
      meetupId: parseInt(meetupId, 10),
      name: data.name,
      email: data.email,
      message: data.message
    });
    return response.data;
  },

  // Alias for backward compatibility
  sendMeetupInvitation: async (meetupId: string, data: {
    friendIds?: string[];
    groupIds?: string[];
    emails?: string[];
    message?: string;
  }) => {
    return meetupApi.inviteToMeetup(meetupId, data);
  },

  // Get my pending invitations
  getMyInvitations: async () => {
    const response = await apiClient.api.get(`/v1/meetups/invitations/my`);
    return response.data.data;
  },

  // Get my guest invitations
  getMyGuestInvitations: async () => {
    const response = await apiClient.api.get(`/v1/guest/invitations/my`);
    return response.data.data;
  },

  respondToGuestInvitation: async (token: string, response: 'accepted' | 'declined') => {
    const result = await apiClient.api.post(`/v1/guest/invitation/${token}/respond`, { response });
    return result.data;
  },

  // Respond to an invitation (accept or decline)
  respondToInvitation: async (invitationId: string, response: 'accept' | 'decline') => {
    const result = await apiClient.api.post(`/v1/meetups/invitations/${invitationId}/respond`, { response });
    return result.data;
  },

  // Get invitations I've sent
  getMySentInvitations: async () => {
    const response = await apiClient.api.get(`/v1/meetups/invitations/sent`);
    return response.data.data;
  },

  // Delete/cancel an invitation
  deleteInvitation: async (invitationId: string) => {
    const result = await apiClient.api.delete(`/v1/meetups/invitations/${invitationId}`);
    return result.data;
  },

  // Get cuisine preferences for all participants of a meetup
  getMeetupCuisinePreferences: async (meetupId: string) => {
    const response = await apiClient.api.get(`/v1/meetups/${meetupId}/cuisine-preferences`);
    return response.data.data;
  },

  // ========== PLACE SELECTION FEATURES ==========

  // Select/update place for a meetup
  selectPlace: async (meetupId: string, placeData: {
    google_place_id: string;
    place_name: string;
    place_address: string;
    place_details: {
      location: { lat: number; lng: number };
      rating?: number;
      price_level?: number;
      types?: string[];
      phone?: string;
      website?: string;
      photo_url?: string;
      opening_hours?: {
        open_now?: boolean;
        periods?: Array<{ open?: { day: number; time: string }; close?: { day: number; time: string } }>;
        weekday_text?: string[];
      };
    };
    notify_participants?: boolean;
    require_confirmation?: boolean;
  }) => {
    const response = await apiClient.api.put(`/v1/meetups/${meetupId}/place`, placeData);
    return response.data.data;
  },

  // Clear/unselect place (return to suggestions)
  clearPlace: async (meetupId: string, options: { notify_participants?: boolean } = {}) => {
    const response = await apiClient.api.delete(`/v1/meetups/${meetupId}/place`, { data: options });
    return response.data.data;
  },

  // Get selected place details
  getPlaceDetails: async (meetupId: string) => {
    const response = await apiClient.api.get(`/v1/meetups/${meetupId}/place`);
    return response.data.data;
  },

  // Confirm place acknowledgment
  confirmPlace: async (meetupId: string, confirmationData: {
    confirmation_method: 'app' | 'email' | 'sms' | 'calendar';
    eta_minutes?: number;
    transportation_mode?: 'driving' | 'walking' | 'transit' | 'rideshare';
  }) => {
    const response = await apiClient.api.post(`/v1/meetups/${meetupId}/place/confirm`, confirmationData);
    return response.data.data;
  },

  // Suggest alternative place
  suggestPlace: async (meetupId: string, suggestionData: {
    google_place_id: string;
    place_name: string;
    place_address: string;
    place_details: {
      location?: { lat: number; lng: number };
      rating?: number;
      price_level?: number;
      types?: string[];
      phone?: string;
      website?: string;
      photo_url?: string;
      [key: string]: unknown;
    };
    reason?: string;
  }) => {
    const response = await apiClient.api.post(`/v1/meetups/${meetupId}/place/suggest`, suggestionData);
    return response.data.data;
  },

  // Update arrival status
  updateArrivalStatus: async (meetupId: string, statusData: {
    status: 'not_started' | 'on_way' | 'arrived' | 'running_late' | 'not_coming';
    eta_minutes?: number;
    notes?: string;
  }) => {
    const response = await apiClient.api.put(`/v1/meetups/${meetupId}/arrival-status`, statusData);
    return response.data.data;
  },

  // Get calendar integration links
  getCalendarLinks: async (meetupId: string) => {
    const response = await apiClient.api.get(`/v1/meetups/${meetupId}/calendar/links`);
    return response.data.data;
  },

  // Download ICS calendar file
  downloadICS: async (meetupId: string): Promise<Blob> => {
    const response = await apiClient.api.get(`/v1/meetups/${meetupId}/calendar/ics`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Get directions
  getDirections: async (meetupId: string, directionsData: {
    origin: { lat: number; lng: number };
    destination: { lat: number; lng: number };
    mode?: 'driving' | 'walking' | 'transit' | 'bicycling';
  }) => {
    const response = await apiClient.api.post(`/v1/meetups/${meetupId}/directions`, directionsData);
    return response.data.data;
  },

  // Find nearby parking
  findParking: async (meetupId: string, location: {
    lat: number;
    lng: number;
    radius?: number;
  }) => {
    const params = new URLSearchParams({
      lat: location.lat.toString(),
      lng: location.lng.toString(),
      ...(location.radius && { radius: location.radius.toString() }),
    });
    const response = await apiClient.api.get(`/v1/meetups/${meetupId}/parking?${params.toString()}`);
    return response.data.data;
  },

  // Get real-time participant ETAs
  getParticipantETAs: async (meetupId: string) => {
    const response = await apiClient.api.get(`/v1/meetups/${meetupId}/navigation/etas`);
    return response.data.data;
  },

  // Delete guest invitation (organizer only)
  deleteGuestInvitation: async (guestId: string) => {
    const response = await apiClient.api.delete(`/v1/guest/${guestId}`);
    return response.data;
  },

  // Generate shareable link for guest invitation (organizer only)
  generateGuestInvitationLink: async (guestId: string) => {
    const response = await apiClient.api.post(`/v1/guest/${guestId}/generate-link`);
    return response.data.data;
  },

  // Get filter preferences for a meetup
  getFilterPreferences: async (meetupId: string) => {
    const response = await apiClient.api.get(`/v1/meetups/${meetupId}/filter-preferences`);
    // Backend returns data directly, not wrapped in { data: ... }
    return response.data.data || response.data;
  },

  // Update filter preferences for a meetup
  updateFilterPreferences: async (meetupId: string, preferences: {
    cuisines?: string[];
    priceMin?: number;
    priceMax?: number;
    rating?: number;
    openAtMeetupTime?: boolean;
    searchRadius?: number;
    accessibility?: string[];
    atmosphere?: string[];
    dietaryRestrictions?: string[];
  }) => {
    const response = await apiClient.api.put(`/v1/meetups/${meetupId}/filter-preferences`, preferences);
    // Backend returns { success: true } directly, not wrapped in { data: ... }
    return response.data.data || response.data;
  },

  // Get dashboard stats for the current user
  getMeetupStats: async () => {
    const response = await apiClient.api.get('/v1/meetups/stats');
    return response.data.data;
  },
}; 
